import { ReactNode } from 'react';
import { SearchPracticeTreatment } from '../../../../../types';
import { StyledDollar } from './DentalPricing.styles';

export const LOW_BRACKET = 20000;

export const MID_BRACKET = 25000;

export const DENTAL_CODE_TYPE = 'ADA';

export const DENTAL_CODES = ['011', '114'] as const;

export type DentalPriceBracket = 'low' | 'med' | 'high';

export const getDentalPricingBracket = (
  treatments?: SearchPracticeTreatment[],
): DentalPriceBracket | null => {
  if (!treatments) return null;

  const { sum, missingTreatment } = DENTAL_CODES.reduce<{ sum: number; missingTreatment: boolean }>(
    ({ sum, missingTreatment }, code) => {
      const priceableTreatment = treatments.find(
        ({ treatment }) => treatment.codeType === DENTAL_CODE_TYPE && treatment.code === code,
      );

      return {
        sum: sum + (priceableTreatment?.price ?? 0),
        missingTreatment: missingTreatment || !priceableTreatment || !priceableTreatment.price,
      };
    },
    { sum: 0, missingTreatment: false },
  );

  if (missingTreatment) return null;

  if (sum < LOW_BRACKET) return 'low';
  if (sum > MID_BRACKET) return 'high';
  return 'med';
};

const dollarBracket = {
  low: (
    <>
      <StyledDollar isGreen={true}>$</StyledDollar>
      <StyledDollar isGreen={false}>$$</StyledDollar>
    </>
  ),
  med: (
    <>
      <StyledDollar isGreen={true}>$$</StyledDollar>
      <StyledDollar isGreen={false}>$</StyledDollar>
    </>
  ),
  high: <StyledDollar isGreen={true}>$$$</StyledDollar>,
};

const bracketText: { [key in DentalPriceBracket]: string } = {
  low: `below $${LOW_BRACKET / 100}`,
  med: `$${LOW_BRACKET / 100} - $${MID_BRACKET / 100}`,
  high: `above $${MID_BRACKET / 100}`,
};

const bracketNumber: { [key in DentalPriceBracket]: string } = {
  low: `<$${LOW_BRACKET / 100}`,
  med: `$${LOW_BRACKET / 100}-$${MID_BRACKET / 100}`,
  high: `>$${MID_BRACKET / 100}`,
};

export const formatDentalPricingNumber = (bracket: DentalPriceBracket): string =>
  bracketNumber[bracket];

export const formatDentalPricing = (bracket: DentalPriceBracket): string => bracketText[bracket];

export const getDentalPricingDollar = (bracket: DentalPriceBracket): ReactNode =>
  dollarBracket[bracket];
